import React from 'react';
import { useTranslation } from 'react-i18next';
import Reveal from './Reveal';

const INPUT_CLASS =
    'w-full px-4 py-3 min-h-[48px] rounded-xl border border-line bg-mist text-ink text-[0.95rem] ' +
    'placeholder:text-ink-soft/60 transition-colors duration-200 ' +
    'focus:outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20';

const SOCIALS = [
    { icon: 'fab fa-facebook-f', href: 'https://facebook.com', key: 'social-facebook' },
    { icon: 'fab fa-instagram', href: 'https://www.instagram.com/diet.sarakhedr/', key: 'social-instagram' },
    { icon: 'fab fa-linkedin-in', href: 'https://linkedin.com', key: 'social-linkedin' },
    { icon: 'fab fa-youtube', href: 'https://youtube.com', key: 'social-youtube' },
];

const ContactSection = ({ t, form, handleFormChange, handleFormSubmit, submitting, notification }) => {
    useTranslation(); // re-render on language change

    const infoRows = [
        { icon: 'fas fa-phone-alt', label: t('contact-phone-label'), value: t('contact-phone-number'), dir: 'ltr' },
        { icon: 'fas fa-envelope', label: t('contact-email-label'), value: t('contact-email-address'), dir: 'ltr' },
        { icon: 'fas fa-map-marker-alt', label: t('contact-location'), value: t('contact-location-address') },
        { icon: 'fas fa-clock', label: t('contact-hours'), value: t('contact-hours-schedule') },
    ];

    return (
        <section id="contact" className="relative bg-white py-16 md:py-24 border-t border-line">
            <div className="sk-container">
                <Reveal className="max-w-[640px] mb-12">
                    <p className="sk-eyebrow">{t('contact-form-title')}</p>
                    <h2 className="mt-3 font-heading font-bold text-primary-deep text-[clamp(1.6rem,3.5vw,2.4rem)] leading-tight">
                        {t('contact-form-subtitle')}
                    </h2>
                </Reveal>

                <div className="grid grid-cols-1 md:grid-cols-[0.9fr_1.1fr] gap-6 lg:gap-10 items-stretch">
                    {/* Info panel — deep brand surface */}
                    <Reveal variant="left" className="h-full">
                        <div className="h-full rounded-[var(--sk-radius-lg)] bg-primary-deep text-white p-6 sm:p-8 flex flex-col gap-1 shadow-[var(--sk-shadow-2)] relative overflow-hidden">
                            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/5 pointer-events-none" aria-hidden="true" />
                            <div className="absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-secondary/10 pointer-events-none" aria-hidden="true" />

                            <ul className="list-none m-0 p-0 flex flex-col gap-5 relative z-10">
                                {infoRows.map((row) => (
                                    <li key={row.label} className="flex items-center gap-4">
                                        <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 text-secondary flex-shrink-0">
                                            <i className={row.icon} aria-hidden="true" />
                                        </span>
                                        <span className="leading-snug">
                                            <span className="block text-[0.75rem] font-semibold uppercase tracking-wider text-white/60">
                                                {row.label}
                                            </span>
                                            <span className="block text-[0.95rem] font-medium text-white mt-0.5" dir={row.dir}>
                                                {row.value}
                                            </span>
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-auto pt-8 relative z-10">
                                <p className="text-[0.85rem] font-semibold text-white/70 mb-3">{t('follow-sara')}</p>
                                <div className="flex gap-2.5">
                                    {SOCIALS.map((s) => (
                                        <a
                                            key={s.key}
                                            href={s.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={t(s.key)}
                                            className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 text-white transition-all duration-200 hover:bg-secondary hover:-translate-y-0.5"
                                        >
                                            <i className={s.icon} aria-hidden="true" />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    {/* Form card */}
                    <Reveal variant="right" delay={100}>
                        <form
                            onSubmit={handleFormSubmit}
                            className="sk-card !rounded-[var(--sk-radius-lg)] p-6 sm:p-8 flex flex-col gap-5"
                            noValidate={false}
                        >
                            {/* Honeypot — hidden from real users */}
                            <input
                                type="checkbox"
                                name="botcheck"
                                tabIndex="-1"
                                autoComplete="off"
                                aria-hidden="true"
                                style={{ display: 'none' }}
                                onChange={handleFormChange}
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="name" className="block font-heading font-semibold text-primary-deep text-[0.88rem] mb-1.5">
                                        {t('form-name-arabic')} <span className="text-danger" aria-hidden="true">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={form.name}
                                        onChange={handleFormChange}
                                        placeholder={t('form-name-placeholder')}
                                        autoComplete="name"
                                        required
                                        className={INPUT_CLASS}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block font-heading font-semibold text-primary-deep text-[0.88rem] mb-1.5">
                                        {t('form-email-arabic')} <span className="text-danger" aria-hidden="true">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleFormChange}
                                        placeholder={t('form-email-placeholder')}
                                        autoComplete="email"
                                        required
                                        className={INPUT_CLASS}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="phone" className="block font-heading font-semibold text-primary-deep text-[0.88rem] mb-1.5">
                                        {t('form-phone')}
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleFormChange}
                                        placeholder="+90 555 000 00 00"
                                        autoComplete="tel"
                                        className={INPUT_CLASS}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="interest" className="block font-heading font-semibold text-primary-deep text-[0.88rem] mb-1.5">
                                        {t('form-select-interest')} <span className="text-danger" aria-hidden="true">*</span>
                                    </label>
                                    <select
                                        id="interest"
                                        name="interest"
                                        value={form.interest}
                                        onChange={handleFormChange}
                                        required
                                        className={`${INPUT_CLASS} cursor-pointer`}
                                    >
                                        <option value="">{t('form-select-interest')}</option>
                                        <option value="consultation">{t('form-option-consultation')}</option>
                                        <option value="monthly">{t('form-option-monthly')}</option>
                                        <option value="premium">{t('form-option-premium')}</option>
                                        <option value="questions">{t('form-option-questions')}</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="message" className="block font-heading font-semibold text-primary-deep text-[0.88rem] mb-1.5">
                                    {t('form-message-arabic')} <span className="text-danger" aria-hidden="true">*</span>
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows="5"
                                    value={form.message}
                                    onChange={handleFormChange}
                                    placeholder={t('form-message-placeholder')}
                                    required
                                    className={`${INPUT_CLASS} resize-none`}
                                />
                            </div>

                            {notification && (
                                <p
                                    role="alert"
                                    className={`m-0 px-4 py-3 rounded-xl text-[0.92rem] font-medium ${
                                        notification.type === 'success'
                                            ? 'bg-primary-soft text-primary-deep'
                                            : 'bg-red-50 text-danger'
                                    }`}
                                >
                                    <i
                                        className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}
                                        aria-hidden="true"
                                    />
                                    {notification.message}
                                </p>
                            )}

                            <button type="submit" disabled={submitting} className="sk-btn sk-btn--primary w-full disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none">
                                {submitting ? (
                                    <i className="fas fa-circle-notch fa-spin" aria-hidden="true" />
                                ) : (
                                    <i className="fas fa-paper-plane rtl:-scale-x-100" aria-hidden="true" />
                                )}
                                {t('form-submit')}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
